// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ConnectedApplication,
  getConnectedApplications,
} from '@/account/api/get-connected-applications';
import { ConnectedApplicationRow } from '@/account/components/connected-application-row';
import { CONNECTED_APPLICATIONS_QUERY_KEY } from '@/account/constants/connected-applications';
import { useWithdrawConnectedApplication } from '@/account/hooks/use-withdraw-connected-application';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useState } from 'react';

interface Props {
  organizationName: string;
}

/** Lists the applications a person connected to their organization, and disconnects them. */
export function ConnectedApplicationsCard(props: Props) {
  const [selected, setSelected] = useState<ConnectedApplication | null>(null);
  const { data, isError } = useQuery({
    queryKey: CONNECTED_APPLICATIONS_QUERY_KEY,
    queryFn: getConnectedApplications,
  });
  const withdraw = useWithdrawConnectedApplication();

  const confirmWithdraw = () => {
    if (selected === null) {
      return;
    }

    withdraw.mutate(selected.key, { onSuccess: () => setSelected(null) });
  };

  // A disconnect in flight keeps its dialog open, so its answer can only close that dialog.
  const closeWithdraw = () => {
    if (withdraw.isPending) {
      return;
    }

    setSelected(null);
  };

  // The card only appears once the person has connected something, or the list fails to load.
  const isHidden = !isError && (data === undefined || data.data.length === 0);

  return (
    <>
      <WithdrawConnectedApplicationDialog
        application={selected}
        organizationName={props.organizationName}
        isPending={withdraw.isPending}
        onClose={closeWithdraw}
        onConfirm={confirmWithdraw}
      />
      {!isHidden && (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Stack paddingX={2} paddingY={2} bgcolor="grey.50">
            <Typography variant="overline">Connected applications</Typography>
          </Stack>
          <Divider />
          {isError && (
            <Alert severity="error" sx={{ margin: 2 }}>
              Could not load your connected applications
            </Alert>
          )}
          {data?.data.map((application, index) => (
            <Fragment key={application.key}>
              {index > 0 && <Divider />}
              <ConnectedApplicationRow
                application={application}
                onDisconnect={() => setSelected(application)}
              />
            </Fragment>
          ))}
        </Paper>
      )}
    </>
  );
}

interface WithdrawDialogProps {
  application: ConnectedApplication | null;
  organizationName: string;
  isPending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function WithdrawConnectedApplicationDialog(props: WithdrawDialogProps) {
  // The dialog keeps naming the application while it fades out after being closed.
  const [shownApplication, setShownApplication] = useState(props.application);
  if (props.application !== null && props.application !== shownApplication) {
    setShownApplication(props.application);
  }

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={props.application !== null}
      onClose={props.onClose}
    >
      <DialogTitle>Disconnect {shownApplication?.name}</DialogTitle>
      <DialogContent dividers>
        <Typography>
          {shownApplication?.name} will lose access to{' '}
          <b>{props.organizationName}</b> right away. To use it again, connect
          it again.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={props.onClose} disabled={props.isPending}>
          No, cancel
        </Button>
        <Button
          size="small"
          color="error"
          variant="contained"
          onClick={props.onConfirm}
          disabled={props.isPending}
        >
          Yes, disconnect
        </Button>
      </DialogActions>
    </Dialog>
  );
}
